import { createContext } from 'react'
import type { UseMutationResult } from '@tanstack/react-query'
import type { Brand } from '../../../types/brand'
import type { CategoryItem } from '../../../gateways/breadstack/categoryApis'
import type { TagItem } from '../../../gateways/breadstack/tagApis'
import type { Product, ProductDraft, StoreType, VariationGroupItem } from '../../../types/product'
import type { WHLocation } from '../../../types/warehouse'
import type { useProductEditor } from '../hooks/useProductEditor'

export interface ProductDetailContextValue {
  storeId: string
  storeType: StoreType

  // True when the editor is in create mode (productId === 'new'). The detail and
  // variation-group queries are disabled and the editor starts from a blank draft.
  isNew: boolean
  // Store warehouses/locations — the source of `warehouse_id` for create.
  warehouses: WHLocation[]

  product: Product | undefined
  isLoading: boolean
  isFetching: boolean

  brands: Brand[]
  brands_map: Record<string, Brand>

  categories: CategoryItem[]
  categories_map: Record<string, CategoryItem>

  tags: TagItem[]
  tags_map: Record<number, TagItem>

  variationGroup: VariationGroupItem[]
  isLoadingVariationGroup: boolean

  covaEditUrl?: string

  editor: ReturnType<typeof useProductEditor>

  updateMutation: UseMutationResult<
    Product,
    Error,
    { productId: string; data: Partial<ProductDraft> },
    unknown
  >

  createMutation: UseMutationResult<
    Product,
    Error,
    { data: Partial<ProductDraft> },
    unknown
  >
}

export const ProductDetailContext = createContext<ProductDetailContextValue | undefined>(undefined)
